/**
 * Derives Configuration list page API routes from `fetchRoute` (same base as createCrudModel).
 * Convention: export → `{fetchRoute}/export`, tasks → `{fetchRoute}/tasks/paged`.
 */

export function resolveConfigExportRoute(fetchRoute) {
  if (!fetchRoute) return null;
  return `${fetchRoute}/export`;
}

export function resolveConfigInternalTasksDefaults({ fetchRoute, namespace }) {
  if (!fetchRoute || !namespace) return null;
  return {
    listRoute: `${fetchRoute}/tasks/paged`,
    filtersModelName: `${namespace}InternalTasks`,
  };
}

/**
 * Resolves `exportRoute` and `internalTasks` for a list descriptor.
 * Explicit values win; `internalTasks: false` disables tasks even when export is enabled.
 * Partial `internalTasks` merges with defaults only when export is enabled (`withExport !== false`).
 */
export function resolveConfigListRoutes({
  fetchRoute,
  namespace,
  withExport = true,
  exportRoute,
  internalTasks,
}) {
  const exportEnabled = withExport !== false;

  let resolvedExportRoute = exportRoute;
  if (!resolvedExportRoute && exportEnabled && fetchRoute) {
    resolvedExportRoute = resolveConfigExportRoute(fetchRoute);
  }

  let resolvedInternalTasks;
  if (internalTasks === false) {
    resolvedInternalTasks = undefined;
  } else if (internalTasks != null) {
    if (exportEnabled && fetchRoute && namespace) {
      const defaults = resolveConfigInternalTasksDefaults({ fetchRoute, namespace });
      resolvedInternalTasks = { ...defaults, ...internalTasks };
    } else {
      resolvedInternalTasks = internalTasks;
    }
  } else if (exportEnabled && fetchRoute && namespace) {
    resolvedInternalTasks = resolveConfigInternalTasksDefaults({ fetchRoute, namespace });
  }

  return {
    exportRoute: resolvedExportRoute,
    internalTasks: resolvedInternalTasks,
  };
}

/**
 * Merges derived routes into a ConfigListPage descriptor (non-destructive for other fields).
 */
export function resolveConfigListDescriptor(descriptor) {
  if (!descriptor) return descriptor;

  const { fetchRoute, namespace, withExport, exportRoute, internalTasks, ...rest } = descriptor;
  const routes = resolveConfigListRoutes({
    fetchRoute,
    namespace,
    withExport,
    exportRoute,
    internalTasks,
  });

  return {
    ...rest,
    namespace,
    fetchRoute,
    withExport,
    ...routes,
  };
}
